import { useState, useEffect, useCallback } from "react"
import {
  initializeWithFallback,
  onNewConversation,
  onReceiveConversations,
  onReceiveMessage,
  onJoinedConversation,
  onMessageRead,
  getConversationsViaHub,
  joinConversation,
  sendMessageViaHub,
  markAsReadViaHub,
  disconnectSignalR,
  offReceiveMessage,
  offJoinedConversation,
  offNewConversation,
  offReceiveConversations,
  offMessageRead,
  getConnectionState,
  restartConnection,
} from "../../services/signalRService"
import {
  debugApiEndpoints,
  getConversationsREST,
  markMessagesAsReadREST,
  isProfileInConversation,
} from "../../services/messageService"
import type { ConversationDto, MessageDto, MarkAsReadResultDto } from "../../types/message"

interface UseChatSignalRProps {
  userId: string
  userProfileId: number | undefined
  conversationId?: string
  onError?: (error: string) => void
}

const useChatSignalR = ({ userId, userProfileId, conversationId, onError }: UseChatSignalRProps) => {
  const [conversations, setConversations] = useState<ConversationDto[]>([])
  const [selectedConversation, setSelectedConversation] = useState<ConversationDto | null>(null)
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null)
  const [messages, setMessages] = useState<MessageDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [connectionInitialized, setConnectionInitialized] = useState(false)
  const [connectionState, setConnectionState] = useState<string>("Not initialized")

  // Set error message
  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    },
    [onError],
  )

  // Join the conversation via SignalR
  const joinConversationRoom = useCallback(
    async (convId: number) => {
      if (!userProfileId) return false

      try {
        const success = await joinConversation(convId, userProfileId)
        return success
      } catch (error) {
        console.error("Error joining conversation room:", error)
        return false
      }
    },
    [userProfileId],
  )

  // Mark messages as read
  const markMessagesAsRead = useCallback(
    async (conversation: ConversationDto) => {
      if (!userProfileId) return false

      try {
        // Intentar marcar todos los mensajes como leídos de una vez
        const success = await markMessagesAsReadREST(conversation.conversationId, userProfileId)

        if (!success) {
          // Si falla, marcar mensajes como leídos uno por uno a través de SignalR
          const unreadMessages = conversation.messages.filter(
            (m) => !m.isRead && m.senderProfileId !== userProfileId && m.id !== undefined,
          )

          for (const message of unreadMessages) {
            await markAsReadViaHub(message.id, userProfileId)
          }
        }
        return true
      } catch (error) {
        console.error("Error marking messages as read:", error)
        return false
      }
    },
    [userProfileId],
  )

  // Load conversations
  const loadConversations = useCallback(
    async (profileId: number) => {
      setLoading(true)
      try {
        // Para depuración, ejecutamos la función que prueba los endpoints REST
        await debugApiEndpoints(profileId)

        // Intentamos obtener las conversaciones a través de SignalR
        let conversationsData: ConversationDto[] = []

        try {
          conversationsData = await getConversationsViaHub(profileId)
        } catch (signalRError) {
          console.error("Error getting conversations via SignalR:", signalRError)
        }

        // Si no hay datos de SignalR, intentar con REST
        if (!conversationsData || conversationsData.length === 0) {
          const restConversations = await getConversationsREST(profileId)
          if (restConversations && restConversations.length > 0) {
            conversationsData = restConversations
          }
        }

        if (conversationsData && conversationsData.length > 0) {
          // Asegurarse de que cada conversación tenga un array de mensajes
          const processedConversations = conversationsData.map((conv) => ({
            ...conv,
            messages: conv.messages || [],
          }))

          // Ordenar conversaciones por fecha de última actividad
          processedConversations.sort(
            (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime(),
          )

          setConversations(processedConversations)

          // Si hay un conversationId en la URL, seleccionar esa conversación
          if (conversationId) {
            const conversationIdNum = Number.parseInt(conversationId)
            const selectedConv = processedConversations.find((c) => c.conversationId === conversationIdNum)

            if (selectedConv) {
              setSelectedConversation(selectedConv)
              setSelectedConversationId(selectedConv.conversationId)

              // Ordenar mensajes por fecha
              const sortedMessages = [...selectedConv.messages].sort(
                (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
              )
              setMessages(sortedMessages)

              // Unirse a la sala de conversación
              await joinConversationRoom(conversationIdNum)

              // Marcar mensajes como leídos
              await markMessagesAsRead(selectedConv)
            } else {
              // Si no se encuentra la conversación en la lista, verificar si el perfil pertenece a la conversación
              const isInConversation = await isProfileInConversation(profileId, conversationIdNum)
              if (isInConversation) {
                // Unirse a la sala de conversación para recibir mensajes
                await joinConversationRoom(conversationIdNum)
              } else {
                console.warn("Profile is not in the requested conversation")
                handleError("No tienes acceso a esta conversación")
              }
            }
          }

          setError(null)
        } else {
          console.warn("No conversations data returned")
          setConversations([])
          handleError("No se encontraron conversaciones. Intenta de nuevo más tarde.")
        }
      } catch (error: any) {
        console.error("Error loading conversations:", error)
        handleError(`Error al cargar conversaciones: ${error.message || "Error desconocido"}`)
        setConversations([])
      } finally {
        setLoading(false)
      }
    },
    [conversationId, joinConversationRoom, markMessagesAsRead, handleError],
  )

  // Initialize SignalR connection
  const initializeSignalR = useCallback(async () => {
    try {
      const success = await initializeWithFallback(userId)

      if (success) {
        setConnectionInitialized(true)
        setConnectionState("Connected")
        if (userProfileId) {
          await loadConversations(userProfileId)
        }
        return true
      } else {
        setConnectionState("Failed")
        handleError("No se pudo establecer la conexión con el servidor.")
        setLoading(false)
        return false
      }
    } catch (error: any) {
      console.error("Error during initialization:", error)
      handleError("Error al conectar con el servidor.")
      setLoading(false)
      return false
    }
  }, [userId, userProfileId, loadConversations, handleError])

  const retryConnection = useCallback(async () => {
    if (!userProfileId) return false

    setLoading(true)
    setError(null)

    // Verificar el estado de la conexión
    const currentState = getConnectionState()
    setConnectionState(currentState)

    // Si la conexión no está activa, reiniciarla
    if (currentState !== "Connected") {
      const success = await restartConnection()
      if (success) {
        setConnectionState("Connected")
        setConnectionInitialized(true)
      } else {
        console.error("Failed to restart SignalR connection")
        handleError("No se pudo establecer la conexión con el servidor. Intente nuevamente.")
        setLoading(false)
        return false
      }
    }

    await loadConversations(userProfileId)
    return true
  }, [userProfileId, loadConversations, handleError])

  const sendMessage = useCallback(
    async (message: string): Promise<void> => {
      if (!message.trim() || !selectedConversation || !userProfileId) return

      const conversationIdNum = selectedConversation.conversationId

      try {
        const success = await sendMessageViaHub(conversationIdNum, userProfileId, message.trim(), "Texto")
        if (!success) {
          handleError("No se pudo enviar el mensaje. Intente nuevamente.")
        }
      } catch (error: any) {
        console.error("Error sending message:", error)
        handleError(`Error al enviar el mensaje: ${error.message || "Error desconocido"}`)
      }
    },
    [selectedConversation, userProfileId, handleError],
  )

  const selectConversation = useCallback(
    async (conversation: ConversationDto) => {
      setSelectedConversation(conversation)
      setSelectedConversationId(conversation.conversationId)

      // Ordenar mensajes por fecha
      const sortedMessages = [...conversation.messages].sort(
        (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
      )
      setMessages(sortedMessages)

      // Unirse a la sala de conversación
      await joinConversationRoom(conversation.conversationId)

      // Marcar mensajes como leídos
      await markMessagesAsRead(conversation)

      return true
    },
    [joinConversationRoom, markMessagesAsRead],
  )

  useEffect(() => {
    if (!connectionInitialized) return

    const handleReceiveMessage = (message: MessageDto) => {
      setConversations((prev) => {
        const conversationExists = prev.some((c) => c.conversationId === message.conversationId)

        if (!conversationExists) {
          console.log(
            `Conversation ${message.conversationId} not found in current list. This might be a new conversation.`,
          )
        }

        return prev.map((conv) => {
          if (conv.conversationId === message.conversationId) {
            // Verificar si el mensaje ya existe en la conversación
            const messageExists = conv.messages.some((m) => m.id === message.id)
            if (messageExists) return conv

            // Añadir el nuevo mensaje y actualizar lastMessage
            return {
              ...conv,
              messages: [...conv.messages, message],
              lastMessage: message,
              lastActivityAt: message.sentAt, // Actualizar la fecha de última actividad
              unreadCount: message.senderProfileId !== userProfileId ? conv.unreadCount + 1 : conv.unreadCount,
            }
          }
          return conv
        })
      })

      // Si el mensaje es para la conversación seleccionada, añadirlo a los mensajes
      if (selectedConversationId === message.conversationId) {
        setMessages((prev) => {
          const messageExists = prev.some((m) => m.id === message.id)
          if (messageExists) return prev

          const newMessages = [...prev, message]

          if (message.senderProfileId !== userProfileId && message.id !== undefined) {
            markAsReadViaHub(message.id, userProfileId!)
          }

          return newMessages
        })
      }
    }

    const handleNewConversation = (conversation: ConversationDto) => {
      setConversations((prev) => {
        // Verificar si la conversación ya existe
        const conversationExists = prev.some((c) => c.conversationId === conversation.conversationId)
        if (conversationExists) return prev

        return [conversation, ...prev]
      })
    }

    const handleReceiveConversations = (receivedConversations: ConversationDto[]) => {
      const processedConversations = receivedConversations.map((conv) => ({
        ...conv,
        messages: conv.messages || [],
      }))

      setConversations(processedConversations)
    }

    const handleJoinedConversation = (joinedConversationId: number) => {
    }

    const handleMessageRead = (data: MarkAsReadResultDto) => {
      setMessages((prev) => {
        return prev.map((msg) => {
          if (msg.id === data.messageId) {
            return { ...msg, isRead: true }
          }
          return msg
        })
      })

      setConversations((prev) => {
        return prev.map((conv) => {
          if (conv.conversationId === data.conversationId) {
            const updatedMessages = conv.messages.map((msg) => {
              if (msg.id === data.messageId) {
                return { ...msg, isRead: true }
              }
              return msg
            })

            const unreadCount = updatedMessages.filter((m) => !m.isRead && m.senderProfileId !== userProfileId).length

            return {
              ...conv,
              messages: updatedMessages,
              unreadCount,
            }
          }
          return conv
        })
      })
    }

    offReceiveMessage(handleReceiveMessage)
    offNewConversation(handleNewConversation)
    offReceiveConversations(handleReceiveConversations)
    offJoinedConversation(handleJoinedConversation)
    offMessageRead(handleMessageRead)

    onReceiveMessage(handleReceiveMessage)
    onNewConversation(handleNewConversation)
    onReceiveConversations(handleReceiveConversations)
    onJoinedConversation(handleJoinedConversation)
    onMessageRead(handleMessageRead)

    return () => {
      offReceiveMessage(handleReceiveMessage)
      offNewConversation(handleNewConversation)
      offReceiveConversations(handleReceiveConversations)
      offJoinedConversation(handleJoinedConversation)
      offMessageRead(handleMessageRead)
    }
  }, [connectionInitialized, selectedConversationId, userProfileId])

  useEffect(() => {
    if (userId && userProfileId) {
      initializeSignalR()
    }

    return () => {
      if (window.location.pathname.indexOf("/properties/messages") === -1) {
        disconnectSignalR()
      }
    }
  }, [initializeSignalR, userId, userProfileId])

  return {
    conversations,
    selectedConversation,
    selectedConversationId,
    messages,
    loading,
    error,
    connectionInitialized,
    connectionState,
    retryConnection,
    sendMessage,
    selectConversation,
  }
}

export default useChatSignalR
